import { useState } from "react";
import { useLocation } from "react-router-dom";
import { createLead } from "../api/leadApi";
import { useSettings } from "../hooks/useSettings";
import { useCommunityShortName } from "../hooks/useCommunityShortName";
import { toast } from "react-hot-toast";
import { InlineThankYouMessage } from "./InlineThankYouMessage";

const toTitleCase = (value) =>
    String(value)
        .replace(/[-_]+/g, " ")
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

// This form renders on many pages, so the lead's source page comes from the route.
// Nav labels are stored inconsistently ("HOME", "Free"), hence the Title Case pass.
const getSourcePageFromPath = (pathname, navItems) => {
    const path = String(pathname || "/");
    const lowerPath = path.toLowerCase();
    const navItem = (navItems || []).find(
        (item) =>
            item?.status !== false &&
            String(item?.path || "").toLowerCase() === lowerPath
    );
    if (navItem?.label) return toTitleCase(navItem.label);
    // Routes with no nav entry fall back to the first path segment, split on
    // camelCase first so /propertyDetails reads "Property Details".
    const segment = path.split("/").filter(Boolean)[0];
    return segment
        ? toTitleCase(segment.replace(/([a-z0-9])([A-Z])/g, "$1 $2"))
        : "Home";
};

/**
 * @param {boolean} [scrollable=true] - keeps the card's own 80vh scroll region, which is right
 *   in the sticky sidebar. Pass false where the card sits in normal page flow (e.g. the Home
 *   page), so the page scrolls instead of nesting a second, easy-to-miss scrollbar.
 */
export const ContactSidebarForm = ({ scrollable = true }) => {
    const { settings } = useSettings();
    const communityRecordShortName = useCommunityShortName();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [subjectOtherReason, setSubjectOtherReason] = useState("");
    const [preferredTitleOtherReason, setPreferredTitleOtherReason] = useState("");
    const [howDidYouFindUsOtherReason, setHowDidYouFindUsOtherReason] = useState("");

    // Header settings has no shortName field, so the community record is the real source.
    const communityShortName =
        communityRecordShortName || settings?.shortName || settings?.clubName || "the community";

    const subjects = [
        "I would like to get information on property(ies)",
        "I would like to see property(ies)",
        "I would like to sell my home",
        "I would like to buy a home",
        "I am looking for a rental",
        "I would like to rent my unit out",
        "I am looking for property manager for my rental",
        "I would like to be notified when a home is listed for sale",
        "I would like to be notified when a home is listed for rent",
        "I would like to be notified when a home is sold",
        "I would like to be notified when a home is rented",
        "I would like to receive community newsletters",
        "I have a correction for the information posted",
        `I have a suggestion for ${communityShortName} website`,
        `I have a complaint about ${communityShortName} website`,
        `I like ${communityShortName} website`,
        "I have questions, please call me",
        "Other",
    ];

    const locationOptions = [
        `I own a unit in ${communityShortName}`,
        `I am a renter in ${communityShortName}`,
        `I am renting out my unit in ${communityShortName}`,
        `I live in ${communityShortName}`,
        "I live in another state",
        "I live in another country",
    ];

    const findingOptions = [
        "Internet search",
        "A friend/relative recommended",
        "On an advertisement",
        "From a frig magnet",
        "From the HOA management office",
        "From a real estate agent",
        "Other",
    ];

    const [formData, setFormData] = useState({
        subject: [],
        firstName: "",
        lastName: "",
        nameSuffix: "",
        preferredTitle: "",
        cellPhone: "",
        phoneCountryCode: "",
        email: "",
        location: [],
        howDidYouFindUs: "",
        additionalInfo: "",
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
        if (name === "preferredTitle" && value !== "Other") {
            setPreferredTitleOtherReason("");
            if (errors.preferredTitleOtherReason) {
                setErrors((prev) => ({ ...prev, preferredTitleOtherReason: "" }));
            }
        }
        if (name === "howDidYouFindUs" && value !== "Other") {
            setHowDidYouFindUsOtherReason("");
            if (errors.howDidYouFindUsOtherReason) {
                setErrors((prev) => ({ ...prev, howDidYouFindUsOtherReason: "" }));
            }
        }
    };

    const handleCheckboxChange = (field, value, checked) => {
        setFormData((prev) => {
            let arr = [...prev[field]];
            if (checked) {
                arr.push(value);
            } else {
                arr = arr.filter((v) => v !== value);
            }
            return { ...prev, [field]: arr };
        });
        if (field === "subject" && value === "Other" && !checked) {
            setSubjectOtherReason("");
        }
        setErrors((prev) => ({
            ...prev,
            [field]: "",
            ...(field === "subject" && value === "Other" && !checked
                ? { subjectOtherReason: "" }
                : {}),
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (formData.subject.length === 0) newErrors.subject = "Select at least one subject";
        if (formData.subject.includes("Other") && !subjectOtherReason.trim()) {
            newErrors.subjectOtherReason = 'Please specify is required';
        }
        if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
        if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
        if (!formData.preferredTitle) newErrors.preferredTitle = "Preferred title is required";
        if (formData.preferredTitle === "Other" && !preferredTitleOtherReason.trim()) {
            newErrors.preferredTitleOtherReason = 'Please specify is required';
        }
        if (!formData.cellPhone.trim()) newErrors.cellPhone = "Cell phone is required";
        if (!formData.phoneCountryCode.trim()) {
            newErrors.phoneCountryCode = "Phone number country name is required";
        }
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }
        if (formData.location.length === 0) newErrors.location = "At least one location is required";
        if (!formData.howDidYouFindUs) newErrors.howDidYouFindUs = "Please select how you found us";
        if (formData.howDidYouFindUs === "Other" && !howDidYouFindUsOtherReason.trim()) {
            newErrors.howDidYouFindUsOtherReason = 'Please specify is required';
        }
        if (!formData.additionalInfo.trim()) newErrors.additionalInfo = "Additional info is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const subjectOther = subjectOtherReason.trim();
            const preferredTitleOther = preferredTitleOtherReason.trim();
            const howDidYouFindUsOther = howDidYouFindUsOtherReason.trim();
            const payload = {
                ...formData,
                subject: formData.subject.map((item) =>
                    item === "Other" && subjectOther ? `Other: ${subjectOther}` : item
                ),
                subjectOtherReason: subjectOther,
                preferredTitleOtherReason: preferredTitleOther,
                howDidYouFindUsOtherReason: howDidYouFindUsOther,
                howDidYouFindUs:
                    formData.howDidYouFindUs === "Other" && howDidYouFindUsOther
                        ? `Other: ${howDidYouFindUsOther}`
                        : formData.howDidYouFindUs,
                sourcePage: getSourcePageFromPath(location.pathname, settings?.navItems),
            };
            await createLead(payload);
            if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
            setSubmitted(true);
            toast.success("Thank you! We will get back to you soon.");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to submit form");
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            subject: [],
            firstName: "",
            lastName: "",
            nameSuffix: "",
            preferredTitle: "",
            cellPhone: "",
            phoneCountryCode: "",
            email: "",
            location: [],
            howDidYouFindUs: "",
            additionalInfo: "",
        });
        setSubjectOtherReason("");
        setPreferredTitleOtherReason("");
        setHowDidYouFindUsOtherReason("");
        setErrors({});
        setSubmitted(false);
    };

    if (submitted) {
        return (
            <InlineThankYouMessage
                preferredTitle={
                    formData.preferredTitle === "Other" && preferredTitleOtherReason.trim()
                        ? preferredTitleOtherReason.trim()
                        : formData.preferredTitle
                }
                lastName={formData.lastName}
                onSendAnother={resetForm}
            />
        );
    }

    const inputClass = "w-full p-1.5 border rounded text-sm";
    const errorStyle = { color: "#dc2626", fontSize: "0.75rem" };
    const requiredFormErrorStyle = {
        color: "#dc2626",
        fontSize: "0.95rem",
        fontWeight: 700,
        marginTop: "4px",
        marginBottom: "8px",
    };
    const sectionLabel = {
        fontSize: "0.85rem",
        fontWeight: "bold",
        marginBottom: "4px",
        display: "block",
    };
    const showRequiredFormError = Object.values(errors).some(
        (v) => v != null && String(v).trim() !== ""
    );

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px",
                fontFamily: "Tahoma, sans-serif",
                ...(scrollable ? { maxHeight: "80vh", overflowY: "auto" } : {}),
            }}
        >
            <h3
                className="text-2xl uppercase"
                style={{
                    fontFamily: "Tahoma, sans-serif",
                    fontWeight: "bold",
                    marginBottom: "12px",
                    color: "black",
                    textAlign: "left",
                }}
            >
                Contact us
            </h3>
            <form onSubmit={handleSubmit}>
                {/* Subject */}
                <div style={{ marginBottom: "8px" }}>
                    <label style={sectionLabel}>Subject (select all applicable): *</label>
                    {subjects.map((subject, i) => (
                        <label key={i} className="flex items-center gap-1 cursor-pointer" style={{ fontSize: "0.8rem", marginBottom: "2px" }}>
                            <input
                                type="checkbox"
                                checked={formData.subject.includes(subject)}
                                onChange={(e) => handleCheckboxChange("subject", subject, e.target.checked)}
                                style={{ width: "14px", height: "14px" }}
                            />
                            <span>{subject}</span>
                        </label>
                    ))}
                    {errors.subject && <div style={errorStyle}>{errors.subject}</div>}
                    {formData.subject.includes("Other") && (
                        <div style={{ marginTop: "6px" }}>
                            <label style={sectionLabel}>Please specify *</label>
                            <input
                                type="text"
                                name="subjectOtherReason"
                                value={subjectOtherReason}
                                onChange={(e) => {
                                    setSubjectOtherReason(e.target.value);
                                    if (errors.subjectOtherReason) {
                                        setErrors((prev) => ({ ...prev, subjectOtherReason: "" }));
                                    }
                                }}
                                placeholder="Please explain the other subject"
                                className={inputClass}
                            />
                            {errors.subjectOtherReason && <div style={errorStyle}>{errors.subjectOtherReason}</div>}
                        </div>
                    )}
                </div>

                {/* First Name */}
                <div style={{ marginBottom: "6px" }}>
                    <input type="text" name="firstName" placeholder="First name *" value={formData.firstName} onChange={handleChange} className={inputClass} />
                    {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
                </div>

                {/* Last Name */}
                <div style={{ marginBottom: "6px" }}>
                    <input type="text" name="lastName" placeholder="Last name *" value={formData.lastName} onChange={handleChange} className={inputClass} />
                    {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
                </div>

                {/* Preferred Title */}
                <div style={{ marginBottom: "6px" }}>
                    <label style={sectionLabel}>Preferred title: *</label>
                    <div className="flex flex-wrap gap-2" style={{ fontSize: "0.8rem" }}>
                        {["Mr.", "Mrs.", "Ms.", "Miss", "Other"].map((t) => (
                            <label key={t} className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="preferredTitle" value={t} checked={formData.preferredTitle === t} onChange={handleChange} />
                                <span>{t}</span>
                            </label>
                        ))}
                    </div>
                    {errors.preferredTitle && <div style={errorStyle}>{errors.preferredTitle}</div>}
                    {formData.preferredTitle === "Other" && (
                        <div style={{ marginTop: "6px" }}>
                            <label style={sectionLabel}>Please specify *</label>
                            <input
                                type="text"
                                name="preferredTitleOtherReason"
                                value={preferredTitleOtherReason}
                                onChange={(e) => {
                                    setPreferredTitleOtherReason(e.target.value);
                                    if (errors.preferredTitleOtherReason) {
                                        setErrors((prev) => ({ ...prev, preferredTitleOtherReason: "" }));
                                    }
                                }}
                                placeholder="Please explain the other preferred title"
                                className={inputClass}
                            />
                            {errors.preferredTitleOtherReason && <div style={errorStyle}>{errors.preferredTitleOtherReason}</div>}
                        </div>
                    )}
                </div>

                {/* Name Suffix */}
                <div style={{ marginBottom: "6px" }}>
                    <label style={sectionLabel}>Name suffix:</label>
                    <div className="flex flex-wrap gap-2" style={{ fontSize: "0.8rem" }}>
                        {["Jr", "Sr", "I", "II", "III", "IV", "V"].map((s) => (
                            <label key={s} className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="nameSuffix" value={s} checked={formData.nameSuffix === s} onChange={handleChange} />
                                <span>{s}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Cell Phone */}
                <div style={{ marginBottom: "6px" }}>
                    <input type="text" name="cellPhone" placeholder="Cell phone *" value={formData.cellPhone} onChange={handleChange} className={inputClass} />
                    {errors.cellPhone && <div style={errorStyle}>{errors.cellPhone}</div>}
                </div>

                {/* Phone Number Country Name */}
                <div style={{ marginBottom: "6px" }}>
                    <input type="text" name="phoneCountryCode" placeholder="Phone number country name *" value={formData.phoneCountryCode} onChange={handleChange} className={inputClass} />
                    {errors.phoneCountryCode && <div style={errorStyle}>{errors.phoneCountryCode}</div>}
                </div>

                {/* Email */}
                <div style={{ marginBottom: "6px" }}>
                    <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleChange} className={inputClass} />
                    {errors.email && <div style={errorStyle}>{errors.email}</div>}
                </div>

                {/* Location */}
                <div style={{ marginBottom: "6px" }}>
                    <label style={sectionLabel}>Location: *</label>
                    {locationOptions.map((loc, i) => (
                        <label key={i} className="flex items-center gap-1 cursor-pointer" style={{ fontSize: "0.8rem", marginBottom: "2px" }}>
                            <input
                                type="checkbox"
                                checked={formData.location.includes(loc)}
                                onChange={(e) => handleCheckboxChange("location", loc, e.target.checked)}
                                style={{ width: "14px", height: "14px" }}
                            />
                            <span>{loc}</span>
                        </label>
                    ))}
                    {errors.location && <div style={errorStyle}>{errors.location}</div>}
                </div>

                {/* How Did You Find Us */}
                <div style={{ marginBottom: "6px" }}>
                    <label style={sectionLabel}>How did you find us: *</label>
                    <div style={{ fontSize: "0.8rem" }}>
                        {findingOptions.map((opt) => (
                            <label key={opt} className="flex items-center gap-1 cursor-pointer" style={{ marginBottom: "2px" }}>
                                <input type="radio" name="howDidYouFindUs" value={opt} checked={formData.howDidYouFindUs === opt} onChange={handleChange} />
                                <span>{opt}</span>
                            </label>
                        ))}
                    </div>
                    {errors.howDidYouFindUs && <div style={errorStyle}>{errors.howDidYouFindUs}</div>}

                    {formData.howDidYouFindUs === "Other" && (
                        <div style={{ marginTop: "6px" }}>
                            <label style={sectionLabel}>Please specify *</label>
                            <input
                                type="text"
                                name="howDidYouFindUsOtherReason"
                                value={howDidYouFindUsOtherReason}
                                onChange={(e) => {
                                    setHowDidYouFindUsOtherReason(e.target.value);
                                    if (errors.howDidYouFindUsOtherReason) {
                                        setErrors((prev) => ({ ...prev, howDidYouFindUsOtherReason: "" }));
                                    }
                                }}
                                placeholder="Please tell us how you found us"
                                className={inputClass}
                            />
                            {errors.howDidYouFindUsOtherReason && <div style={errorStyle}>{errors.howDidYouFindUsOtherReason}</div>}
                        </div>
                    )}
                </div>

                {/* Additional Info */}
                <div style={{ marginBottom: "8px" }}>
                    <textarea
                        name="additionalInfo"
                        placeholder="Additional info *"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        className={inputClass}
                        rows={3}
                    />
                    {errors.additionalInfo && <div style={errorStyle}>{errors.additionalInfo}</div>}
                </div>

                {showRequiredFormError && (
                    <div style={requiredFormErrorStyle}>
                        Please complete all required fields highlighted above.
                    </div>
                )}
                <button
                    type="submit"
                    disabled={submitting}
                    style={{
                        width: "100%",
                        background: "#BA1500",
                        color: "#fff",
                        border: "none",
                        padding: "8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                    }}
                >
                    {submitting ? "Sending..." : "Send"}
                </button>
            </form>
        </div>
    );
};
